import { useEffect, useRef, useState } from "react";
import { FileHelpers } from "@tldraw/utils";
import type { TLAssetStore, TLStoreSnapshot } from "@tldraw/tlschema";
import { Layers } from "lucide-react";
import { taskQueue } from "../shared/images";
import type { Room } from "./api";
import { createSnapshotEditor } from "./BoardCanvas";

// Fetch and render only one visible board at a time; never open sync connections.
const previewQueue = taskQueue(1);
const previewAssets: TLAssetStore = {
  upload: async () => {
    throw new Error("Read-only preview");
  },
  resolve: (asset) => {
    const src = asset.props.src;
    return asset.type === "image" && src && asset.meta.imagePreviews
      ? `${src}?size=256`
      : src;
  },
};

async function renderPreview(
  snapshot: TLStoreSnapshot,
  signal: AbortSignal,
): Promise<Blob | null> {
  signal.throwIfAborted();
  // Sheet sources use an SVG <image>, which must be embedded for PNG export.
  // Fetch their small server previews serially, without modifying saved records.
  for (const record of Object.values(snapshot.store)) {
    if (
      record.typeName !== "shape" ||
      record.type !== "sheet" ||
      !record.props.preview ||
      record.props.preview.startsWith("data:")
    )
      continue;
    const url = new URL(record.props.preview, location.origin);
    if (
      url.origin === location.origin &&
      url.pathname.startsWith("/api/rooms/")
    )
      url.searchParams.set("size", "256");
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error("Drawing preview unavailable");
    record.props.preview = await FileHelpers.blobToDataUrl(
      await response.blob(),
    );
  }
  signal.throwIfAborted();
  return renderFromSnapshot(snapshot);
}

/** Render the thumbnail from a read-only editor, without mounting a canvas. */
async function renderFromSnapshot(snapshot: TLStoreSnapshot) {
  const editor = createSnapshotEditor(snapshot, previewAssets);
  try {
    // A legacy board may have an empty first page. Use its first populated page.
    const page = editor
      .getPages()
      .find((p) => editor.getPageShapeIds(p.id).size > 0);
    if (!page) return null;
    editor.setCurrentPage(page.id);
    const bounds = editor.getCurrentPageBounds();
    if (!bounds) return null;
    await document.fonts.ready;
    const { blob } = await editor.toImage([...editor.getCurrentPageShapeIds()], {
      format: "png",
      background: true,
      padding: 24,
      scale: Math.min(1, 640 / (bounds.w + 48), 390 / (bounds.h + 48)),
      pixelRatio: 1,
    });
    return blob;
  } finally {
    editor.dispose();
  }
}

export function ProjectPreview({ room }: { room: Room }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [src, setSrc] = useState("");
  const [status, setStatus] = useState("Loading preview…");
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "150px" },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    const controller = new AbortController();
    let url = "";
    void previewQueue(async () => {
      if (controller.signal.aborted) return;
      const signal = AbortSignal.any([
        controller.signal,
        AbortSignal.timeout(30_000),
      ]);
      try {
        const response = await fetch(`/api/rooms/${room.id}/preview`, {
          signal,
          cache: "no-store",
        });
        if (!response.ok) throw new Error("Preview unavailable");
        const snapshot: TLStoreSnapshot = await response.json();
        const blob = await renderPreview(snapshot, signal);
        if (controller.signal.aborted) return;
        if (blob) {
          url = URL.createObjectURL(blob);
          setSrc(url);
        } else setStatus("No sheets or drawings yet");
      } catch {
        if (!controller.signal.aborted)
          setStatus("Preview unavailable · open project");
      }
    });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [room.id, visible]);
  return (
    <div ref={ref} className="project-preview">
      {src ? (
        <img src={src} alt={`Board preview of ${room.name}`} />
      ) : (
        <div className="project-preview-status">
          <Layers size={26} aria-hidden="true" />
          <span>{status}</span>
        </div>
      )}
    </div>
  );
}
